
import React, { useState, useEffect } from 'react';
import { 
  calculateMemorabilityScore, 
  getMemorabilityTier, 
  MemorabilityBreakdown as MemorabilityBreakdownType 
} from '@/utils/memorabilityCalculator';
import MemorabilityBreakdown from '@/components/MemorabilityBreakdown';
import MemorabilityGauge from '@/components/MemorabilityGauge';
import FactorsList from '@/components/FactorsList';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp } from 'lucide-react';
import UserNav from '@/components/UserNav';
import { User } from '@/types/user';
import { AuthForms } from '@/components/AuthForms';
import { useIsMobile } from '@/hooks/use-mobile';

const MemorabilityAnalyzerPage: React.FC = () => {
  const isMobile = useIsMobile();
  const [phrase, setPhrase] = useState('');
  const [results, setResults] = useState<MemorabilityBreakdownType | null>(null);
  const [recentResults, setRecentResults] = useState<Array<{phrase: string, score: number}>>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [showAllResults, setShowAllResults] = useState(false);
  const [showAuthForm, setShowAuthForm] = useState(false);
  const [activeTab, setActiveTab] = useState<'breakdown' | 'factors'>('breakdown');
  
  const INITIAL_DISPLAY_COUNT = 5;

  // Load user data from localStorage/sessionStorage on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('user') || sessionStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error('Error parsing stored user data', e);
      }
    }
  }, []);

  // Load recent results from localStorage
  useEffect(() => {
    // If user is logged in, load user-specific results
    const storageKey = user 
      ? `memorability_results_${user.id}` 
      : 'recent_memorability_results';
      
    const savedResults = localStorage.getItem(storageKey);
    if (savedResults) {
      try {
        setRecentResults(JSON.parse(savedResults));
      } catch (e) {
        console.error('Error parsing stored results', e);
      }
    }
  }, [user]);

  const handleAnalyze = () => {
    if (!phrase.trim()) {
      toast.warning('Please enter a phrase to analyze');
      return;
    }

    setIsAnalyzing(true);
    
    // Add a small delay to show processing (useful for UX feedback)
    setTimeout(() => {
      try {
        const breakdown = calculateMemorabilityScore(phrase);
        setResults(breakdown);
        
        // Save to recent results
        const newRecent = [
          { phrase, score: breakdown.total },
          ...recentResults.filter(item => item.phrase !== phrase)
        ].slice(0, 10); // Keep only 10 most recent
        
        setRecentResults(newRecent);
        
        // Store in localStorage based on user
        const storageKey = user 
          ? `memorability_results_${user.id}` 
          : 'recent_memorability_results';
          
        localStorage.setItem(storageKey, JSON.stringify(newRecent));
        
        setIsAnalyzing(false);
      } catch (error) {
        toast.error('Error analyzing phrase');
        setIsAnalyzing(false);
      }
    }, 300);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleAnalyze();
    }
  };
  
  const handleShowAuth = () => {
    setShowAuthForm(true);
  };
  
  const handleLogin = (loggedInUser: User) => {
    setUser(loggedInUser);
    setShowAuthForm(false);
    toast.success('Successfully logged in');
    
    // Load user-specific results after login
    const userStorageKey = `memorability_results_${loggedInUser.id}`;
    const savedResults = localStorage.getItem(userStorageKey);
    if (savedResults) {
      try {
        setRecentResults(JSON.parse(savedResults));
      } catch (e) {
        console.error('Error parsing stored results', e);
      }
    }
  };

  return (
    <div className="min-h-screen py-8 bg-gradient-to-b from-purple-50 to-white">
      <div className="absolute top-4 right-4 z-10">
        <UserNav 
          user={user}
          onLogout={() => {}} // Handled by sidebar now
          onShowAuth={handleShowAuth}
        />
      </div>

      <div className="container mx-auto px-4">
        {showAuthForm ? (
          <div className="max-w-md mx-auto mt-16">
            <Button 
              variant="outline" 
              className="mb-4" 
              onClick={() => setShowAuthForm(false)}
            >
              Back to Analyzer
            </Button>
            <AuthForms onLogin={handleLogin} />
          </div>
        ) : (
          <>
            <header className="flex justify-between items-center mb-8">
              <div>
                <h1 className="text-3xl font-bold mb-2">MEM-20™ Memorability Analyzer</h1>
                <p className="text-gray-600">
                  Analyze how memorable a brand name, slogan, or phrase is using neurological factors.
                </p>
              </div>
            </header>

            <div className="max-w-4xl mx-auto">
              <div className="mb-8">
                <div className="flex gap-2 mb-4">
                  <Input
                    placeholder="Enter a phrase, slogan, or brand name (e.g., 'Stay close with Stayclos')"
                    value={phrase}
                    onChange={(e) => setPhrase(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="flex-1"
                  />
                  <Button onClick={handleAnalyze} disabled={isAnalyzing}>
                    {isAnalyzing ? 'Analyzing...' : 'Analyze'}
                  </Button>
                </div>
                
                {recentResults.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-500 mb-2">Recent analyses:</p>
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-wrap gap-2">
                        {(showAllResults ? recentResults : recentResults.slice(0, INITIAL_DISPLAY_COUNT)).map((item) => (
                          <Button
                            key={item.phrase}
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setPhrase(item.phrase);
                              const breakdown = calculateMemorabilityScore(item.phrase);
                              setResults(breakdown);
                            }}
                            className="flex items-center gap-1.5"
                          >
                            <span>{item.phrase}</span>
                            <span className="px-1.5 py-0.5 bg-gray-100 rounded-full text-xs">
                              {item.score}
                            </span>
                          </Button>
                        ))}
                      </div>
                      {recentResults.length > INITIAL_DISPLAY_COUNT && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => setShowAllResults(!showAllResults)}
                          className="self-center mt-1 flex items-center gap-1"
                        >
                          {showAllResults ? (
                            <>Show Less <ChevronUp className="h-4 w-4" /></>
                          ) : (
                            <>See All <ChevronDown className="h-4 w-4" /></>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {results && (
                <Card className="mt-6">
                  <CardHeader>
                    <CardTitle>Memorability Score for "{phrase}"</CardTitle>
                    <CardDescription className="flex items-center gap-1.5">
                      {getMemorabilityTier(results.total).emoji} {getMemorabilityTier(results.total).tier}
                      <span className="text-sm opacity-70"> - {results.total}/30</span>
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex flex-col md:flex-row gap-8">
                    <div className="flex-1 flex justify-center">
                      <MemorabilityGauge score={results.total} size={isMobile ? "md" : "lg"} />
                    </div>
                    <div className="flex-1">
                      <div className="flex space-x-1 mb-4">
                        <Button
                          variant={activeTab === 'breakdown' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setActiveTab('breakdown')}
                        >
                          Score Breakdown
                        </Button>
                        <Button
                          variant={activeTab === 'factors' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setActiveTab('factors')}
                        >
                          Factors ({results.factors_triggered.length})
                        </Button>
                      </div>
                      
                      {activeTab === 'breakdown' ? (
                        <MemorabilityBreakdown breakdown={results} />
                      ) : (
                        <FactorsList factors={results.factor_details} />
                      )}
                    </div>
                  </CardContent>
                  <CardFooter className="flex flex-col text-sm text-gray-500">
                    <p className="mb-2">
                      The MEM-20™ Neurological Memorability Score evaluates 20 factors that influence how memorable a phrase is.
                    </p>
                    <p>
                      Our algorithm analyzes three key dimensions of memorability:
                    </p>
                    <ul className="list-disc pl-5 mt-2 space-y-1">
                      <li>
                        <strong>Sound Tricks (0-10)</strong> - How phonetic patterns create memorability
                      </li>
                      <li>
                        <strong>Emotion & Imagery (0-10)</strong> - How the phrase triggers emotional responses
                      </li>
                      <li>
                        <strong>Cognitive Grip (0-10)</strong> - How linguistic patterns help the brain retain the phrase
                      </li>
                    </ul>
                  </CardFooter>
                </Card>
              )}
              
              <div className="mt-8 text-sm text-gray-500">
                <h3 className="font-medium mb-2">What makes a phrase highly memorable?</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Phonetic patterns like alliteration and rhyming</li>
                  <li>Balanced syllable structure (5-8 syllables total)</li>
                  <li>Emotionally resonant words that trigger feelings</li>
                  <li>Strong sound patterns with clear pronunciation</li>
                  <li>Clever linguistic devices like semantic paradoxes</li>
                  <li>Phrases that feel good to say out loud</li>
                  <li>Cultural references that connect to existing knowledge</li>
                </ul>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MemorabilityAnalyzerPage;
